/**
 * CARDIO MODALITY — which aerobic metrics are meaningful for which activity.
 *
 * Cardio is not one thing. Running, incline walking, rucking, an assault bike,
 * a real bike, a rower and a pool each have their own relationship between
 * distance, time and effort, and pooling them produces numbers that describe
 * no real session:
 *
 *   - Pace per mile is only comparable WITHIN a modality. 12:00/mi running and
 *     12:00/mi on a 12% treadmill grade are not the same effort, and neither
 *     resembles 12:00/mi of cycling.
 *   - Machine distance is machine-specific. An assault bike, an erg and a road
 *     bike all report "distance" on incompatible scales.
 *   - VDOT is Jack Daniels' RUNNING metric. It is derived from the running
 *     economy curve (ml/kg/min at a given m/min of running) and is undefined
 *     for anything else.
 *
 * MEASURED CONSEQUENCE (ecorbin791, 2026-08-12): the pace engine computed VDOT
 * from whatever recent effort scored highest, across all cardio. His best was
 * a "Row/Bike Sprint" at VDOT 50.7; his best actual RUN was 31.9. Every
 * training pace on the run screen was therefore anchored ~19 VDOT points too
 * high — a threshold target near 6:00/mi against a true threshold near
 * 9:00/mi. `Elliptical Trainer` and `Treadmill Incline Walk` were likewise
 * classified as running by the call sites' substring checks.
 *
 * WHY A SHARED MODULE: three call sites each had their own ad-hoc ternary
 * chain (`smartWorkoutGenerator`, `getCardioProgressionSummary`,
 * `SingleActivityTracker`) and one hardcoded `'run'` for everything
 * (`WorkoutDisplayV2`). Divergent classifiers are how "Elliptical" became a
 * run. One classifier, one set of rules.
 */

export type CardioModality =
  | 'run'         // Flat-ish running or jogging, treadmill or outdoors
  | 'walk'        // Walking, including incline/treadmill walking
  | 'ruck'        // Loaded march — has its own engine (analyzeRuckProgression)
  | 'bike'        // Road/stationary/assault bike, spin
  | 'row'         // Rower, ski erg
  | 'swim'        // Pool or open water
  | 'elliptical'  // Elliptical, stair climber — locomotion without ground pace
  | 'other';      // Unclassifiable, ambiguous, or mixed-modality

/**
 * Word-boundary match on a normalised name.
 *
 * Deliberately NOT `includes`. "Crunches" contains "run" and appears in real
 * data — that exact false positive polluted the cardio pace pool before
 * `normalizeExerciseMatchKey` was introduced (audit RC-4). A boundary match
 * also stops "Ruck" matching inside longer words.
 */
function has(name: string, ...words: string[]): boolean {
  const n = name.toLowerCase().replace(/[^a-z0-9]+/g, ' ');
  return words.some(w => new RegExp(`(^| )${w}( |$)`).test(n));
}

/**
 * Families that describe HOW the athlete moved. If a name names two of them
 * ("Easy Jog or Bike", "Row/Bike Sprint") the log cannot be attributed, so it
 * must not anchor anything modality-specific.
 */
const FAMILIES: Array<[CardioModality, string[]]> = [
  ['swim', ['swim', 'swimming', 'freestyle', 'laps']],
  ['ruck', ['ruck', 'rucking', 'march', 'rucks']],
  ['row', ['row', 'rowing', 'rower', 'erg', 'skierg', 'ski']],
  ['bike', ['bike', 'biking', 'bicycle', 'cycle', 'cycling', 'spin', 'airdyne', 'echo']],
  ['elliptical', ['elliptical', 'stairmaster', 'stairs', 'stepper', 'stepmill', 'arc']],
  ['walk', ['walk', 'walking', 'hike', 'hiking', 'trek']],
  ['run', ['run', 'running', 'jog', 'jogging', 'sprint', 'sprints']],
];

/**
 * Classify a cardio exercise by name.
 *
 * Returns 'other' when the name is ambiguous, mixed, or simply unspecific
 * ("Easy Cardio" — 17 logs in ecorbin791's history). 'other' is a real answer,
 * not a failure: it means "do not apply modality-specific science to this",
 * which is strictly better than guessing 'run' as the old call sites did.
 */
export function classifyCardioModality(exerciseName: string): CardioModality {
  if (!exerciseName) return 'other';

  const matched = FAMILIES.filter(([, words]) => has(exerciseName, ...words)).map(([m]) => m);
  if (matched.length === 0) return 'other';
  if (matched.length === 1) return matched[0];

  // Multiple families named. Some combinations are genuinely resolvable
  // because one term qualifies the other rather than competing with it.
  const set = new Set(matched);

  // A loaded march is a ruck even when the name also says "walk" or "hike".
  if (set.has('ruck')) return 'ruck';
  // "Treadmill Incline Walk", "Walk/Run intervals" — walking dominates the
  // effort profile and neither should anchor a running VDOT.
  if (set.has('walk') && set.has('run')) return 'walk';

  // Anything else naming two machines or two gaits ("Row/Bike Sprint",
  // "Easy Jog or Bike") is unattributable.
  return 'other';
}

/**
 * Is VDOT defined for this activity?
 *
 * Running only. This is not conservatism — Daniels' tables map running
 * velocity to VO2 via the running economy curve, and applying them to a
 * rower or a bike yields a number with no physiological meaning that then
 * prescribes running paces.
 */
export function supportsVDOT(exerciseName: string): boolean {
  return classifyCardioModality(exerciseName) === 'run';
}

/**
 * May two logs be compared on pace per mile / per km?
 *
 * Same modality only. Grade is a further confound WITHIN walking and running
 * (a 12% incline walk and a flat walk are different efforts at the same pace);
 * incline is tracked separately by the pace engine, and this function does not
 * attempt to resolve it.
 */
export function pacesComparable(a: string, b: string): boolean {
  const ma = classifyCardioModality(a);
  const mb = classifyCardioModality(b);
  if (ma === 'other' || mb === 'other') return false;
  return ma === mb;
}

/**
 * Adapter for `analyzeCardioProgression`'s `exerciseType` argument.
 *
 * That parameter is echoed onto the returned decision and is not used for any
 * science (VDOT gates on the name — see `supportsVDOT`), so passing the honest
 * modality through is strictly better than the old ternaries, one of which
 * defaulted unmatched names to 'swim'.
 */
export function cardioTypeParam(exerciseName: string): CardioModality {
  return classifyCardioModality(exerciseName);
}

/**
 * The unit an athlete would actually recognise for this modality. Used for
 * copy, so a rower is not told about minutes per mile.
 */
export function paceUnitFor(modality: CardioModality): string {
  switch (modality) {
    case 'row': return '/500m';
    case 'swim': return '/100m';
    case 'bike': return 'mph';
    case 'elliptical': return 'min';
    case 'other': return 'min';
    default: return '/mi';
  }
}
